import React, { useCallback, useEffect, useState } from "react";
import { Eye, EyeOff, Phone, Plus, Save, Video } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { PageShell } from "@/pages/dashboard/RolePages";

const EMPTY = {
  name: "",
  description: "",
  session_type: "phone",
  duration_min: 30,
  is_free: false,
  energy_cost: 0,
  assigned_consultor_id: "",
  required_fields: [
    { key: "reason", label: "Motivo de la solicitud", field_type: "textarea", required: true, options: [] },
  ],
  status: "draft",
};

const AccompanimentManager = () => {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState(EMPTY);
  const [editingId, setEditingId] = useState(null);
  const [error, setError] = useState("");
  const [ok, setOk] = useState("");
  const [busy, setBusy] = useState(false);
  const [consultants, setConsultants] = useState([]);
  const [adminDelegated, setAdminDelegated] = useState(false);

  const load = useCallback(async () => {
    try {
      const [{ data }, consultantsResponse, delegationResponse] = await Promise.all([
        api.get("/api/director/acompanamiento/catalog"),
        api.get("/api/admin/acompanamiento/consultants"),
        api.get("/api/director/acompanamiento/delegation"),
      ]);
      setItems(data);
      setConsultants(consultantsResponse.data);
      setAdminDelegated(Boolean(delegationResponse.data.enabled));
    } catch (err) {
      setError(formatApiError(err));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const edit = (item) => {
    setEditingId(item.id);
    setDraft({
      name: item.name,
      description: item.description,
      session_type: item.session_type,
      duration_min: item.duration_min,
      is_free: item.is_free,
      energy_cost: item.energy_cost,
      assigned_consultor_id: item.assigned_consultor_id || "",
      required_fields: item.required_fields || [],
      status: item.status,
    });
    setError(""); setOk("");
  };

  const save = async (event) => {
    event.preventDefault();
    setBusy(true); setError(""); setOk("");
    try {
      if (editingId) {
        await api.put(`/api/director/acompanamiento/catalog/${editingId}`, draft);
      } else {
        await api.post("/api/director/acompanamiento/catalog", draft);
      }
      setOk(editingId ? "Acompañamiento actualizado." : "Acompañamiento creado.");
      setEditingId(null); setDraft(EMPTY);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const setStatus = async (item, status) => {
    setError(""); setOk("");
    try {
      const payload = { ...item, status };
      delete payload.id; delete payload.created_at; delete payload.updated_at;
      await api.put(`/api/director/acompanamiento/catalog/${item.id}`, payload);
      setOk(`Estado actualizado a ${status}.`);
      await load();
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const updateDelegation = async (enabled) => {
    setError(""); setOk("");
    try {
      await api.put("/api/director/acompanamiento/delegation", { enabled });
      setAdminDelegated(enabled);
      setOk(enabled ? "Administrador autorizado para asignar acompañamientos." : "Autorización administrativa revocada.");
    } catch (err) {
      setError(formatApiError(err));
    }
  };

  const updateField = (index, key, value) => {
    const fields = [...draft.required_fields];
    fields[index] = { ...fields[index], [key]: value };
    setDraft({ ...draft, required_fields: fields });
  };

  return (
    <PageShell kicker="Director · Acompañamientos KD9" title="ACOMPAÑAMIENTOS, LLAMADAS Y VIDEOLLAMADAS"
      desc="Catálogo universal: lo publicado es exactamente lo que el usuario puede solicitar."
      testId="director-accompaniment-manager">
      {error && <div role="alert" className="kd9-card p-4 mb-4" style={{ color: "#FCA5A5" }}>{error}</div>}
{ok && <div className="kd9-card p-4 mb-4" style={{ color: "#86EFAC" }}>{ok}</div>}
      <div className="kd9-card p-4 mb-5 flex flex-wrap items-center justify-between gap-3">
        <div><div className="font-cinzel kd9-gold-text">DELEGACIÓN DEL ADMINISTRADOR</div><div className="font-mont text-xs">Dirección conserva el control y puede activar o revocar la asignación administrativa.</div></div>
        <label className="flex items-center gap-2 font-mont text-xs"><input type="checkbox" checked={adminDelegated} onChange={(e) => updateDelegation(e.target.checked)}/> Autorizar asignaciones</label>
      </div>

      <div className="grid xl:grid-cols-[1.1fr_.9fr] gap-6">
        <section className="space-y-3">
          {items.map((item) => (
            <article key={item.id} className="kd9-card p-5">
              <div className="flex flex-wrap items-start gap-3">
                {item.session_type === "phone" ? <Phone /> : <Video />}
                <div className="flex-1 min-w-[220px]">
                  <h2 className="font-cinzel kd9-gold-text">{item.name}</h2>
                  <p className="font-cormorant" style={{ color: "var(--kd9-text-soft)" }}>{item.description}</p>
                  <div className="font-mont text-xs mt-2">
                    {item.duration_min} min · {item.is_free ? "Gratuito · 0 KD" : `${new Intl.NumberFormat("es-CO").format(item.energy_cost)} Energía KD9`}
                  </div>
                  <div className="font-mont text-[10px] uppercase tracking-widest mt-2">{item.status}</div>
                  <div className="font-mont text-[10px] mt-1">Consultor asignado: {consultants.find((row) => row.id === item.assigned_consultor_id)?.name || "Pendiente de configurar"}</div>
                </div>
                <div className="flex flex-wrap gap-2">
                  <button className="kd9-btn-outline px-3 py-2 rounded-lg text-xs" onClick={() => edit(item)}>Editar</button>
                  {item.status !== "published" && <button className="kd9-btn-primary px-3 py-2 rounded-lg text-xs" onClick={() => setStatus(item, "published")}><Eye size={13}/> Publicar</button>}
                  {item.status === "published" && <button className="kd9-btn-outline px-3 py-2 rounded-lg text-xs" onClick={() => setStatus(item, "hidden")}><EyeOff size={13}/> Ocultar</button>}
                  {item.status !== "inactive" && <button className="kd9-btn-outline px-3 py-2 rounded-lg text-xs" onClick={() => setStatus(item, "inactive")}>Desactivar</button>}
                </div>
              </div>
            </article>
          ))}
          {!items.length && <div className="kd9-card p-8 text-center">No hay acompañamientos creados.</div>}
        </section>

        <form onSubmit={save} className="kd9-card p-6 h-fit">
          <h2 className="font-cinzel kd9-gold-text text-xl mb-4">{editingId ? "EDITAR ACOMPAÑAMIENTO" : "NUEVO ACOMPAÑAMIENTO"}</h2>
          <label className="block mb-3 font-mont text-xs">Nombre
            <input className="kd9-input mt-1 pl-3" value={draft.name} required onChange={(e) => setDraft({ ...draft, name: e.target.value })}/>
          </label>
          <label className="block mb-3 font-mont text-xs">Descripción
            <textarea className="kd9-input mt-1 p-3" rows={3} value={draft.description} required onChange={(e) => setDraft({ ...draft, description: e.target.value })}/>
          </label>
          <div className="grid sm:grid-cols-2 gap-3 mb-3">
            <label className="font-mont text-xs">Modalidad
              <select className="kd9-input mt-1 pl-3" value={draft.session_type} onChange={(e) => setDraft({ ...draft, session_type: e.target.value })}>
                <option value="phone">Llamada</option><option value="video_external">Videollamada</option>
              </select>
            </label>
            <label className="font-mont text-xs">Duración (minutos)
              <input type="number" min="5" max="240" className="kd9-input mt-1 pl-3" value={draft.duration_min} onChange={(e) => setDraft({ ...draft, duration_min: Number(e.target.value) })}/>
            </label>
          </div>
          <label className="flex items-center gap-2 font-mont text-xs mb-3">
            <input
              type="checkbox"
              checked={draft.is_free}
              onChange={(e) => setDraft({ ...draft, is_free: e.target.checked, energy_cost: e.target.checked ? 0 : draft.energy_cost })}
            />
            Es gratuito
          </label>
          <label className="block mb-4 font-mont text-xs">Consultor responsable
            <select className="kd9-input mt-1 pl-3" value={draft.assigned_consultor_id || ""} onChange={(e) => setDraft({ ...draft, assigned_consultor_id: e.target.value || null })}>
              <option value="">Asignación manual posterior</option>
              {consultants
                .filter((consultant) => (consultant.supported_session_types || []).includes(draft.session_type))
                .map((consultant) => <option key={consultant.id} value={consultant.id}>{consultant.name} · {consultant.email}</option>)}
            </select>
            <span className="mt-1 block text-[10px]" style={{ color: "var(--kd9-text-soft)" }}>
              Solo aparecen Consultores con la modalidad {draft.session_type === "video_external" ? "Videollamada" : "Llamada"} habilitada por Dirección.
            </span>
          </label>
          {!draft.is_free && <label className="block mb-4 font-mont text-xs">Costo en Energía KD9
            <input
              type="number"
              min="1"
              step="1"
              required
              className="kd9-input mt-1 pl-3"
              value={draft.energy_cost}
              onChange={(e) => setDraft({ ...draft, energy_cost: Number(e.target.value) })}
            />
          </label>}

          <div className="font-cinzel text-sm mb-2">DATOS QUE LLENA EL USUARIO</div>
          {draft.required_fields.map((field, index) => (
            <div key={index} className="grid grid-cols-[1fr_1fr_auto] gap-2 mb-2">
              <input className="kd9-input pl-3" placeholder="Clave" value={field.key} onChange={(e) => updateField(index, "key", e.target.value.replace(/\s+/g, "_").toLowerCase())}/>
              <input className="kd9-input pl-3" placeholder="Etiqueta" value={field.label} onChange={(e) => updateField(index, "label", e.target.value)}/>
              <button type="button" className="kd9-btn-outline px-3 rounded-lg" onClick={() => setDraft({ ...draft, required_fields: draft.required_fields.filter((_, i) => i !== index) })}>×</button>
            </div>
          ))}
          <button type="button" className="kd9-btn-outline px-3 py-2 rounded-lg text-xs mb-4" onClick={() => setDraft({ ...draft, required_fields: [...draft.required_fields, { key: "", label: "", field_type: "text", required: true, options: [] }] })}><Plus size={13}/> Agregar dato</button>
          <label className="block mb-4 font-mont text-xs">Estado inicial
            <select className="kd9-input mt-1 pl-3" value={draft.status} onChange={(e) => setDraft({ ...draft, status: e.target.value })}>
              <option value="draft">Borrador</option><option value="published">Publicado</option><option value="hidden">Oculto</option><option value="inactive">Desactivado</option>
            </select>
          </label>
          <div className="flex gap-2">
            <button disabled={busy} className="kd9-btn-primary flex-1 py-3 rounded-lg"><Save size={14}/> {busy ? "Guardando…" : "Guardar"}</button>
            {editingId && <button type="button" className="kd9-btn-outline px-4 rounded-lg" onClick={() => { setEditingId(null); setDraft(EMPTY); }}>Cancelar</button>}
          </div>
        </form>
      </div>
    </PageShell>
  );
};

export default AccompanimentManager;
